/**
 * TMA i18n entry point.
 *
 * Task #4 spec calls for `@inlang/paraglide-vite`. We use the shared
 * `@astrology/i18n` runtime instead because:
 *   1. TMA is a single-page React app — no SSG locale generation needed.
 *   2. Telegram's WebApp SDK already exposes the user's IETF language tag
 *      via `tg.initData?.user?.language_code` — Paraglide's compile-time
 *      flag-stripping adds no value here.
 *   3. Shared package keeps RU/EN/ES copy in lock-step across all surfaces.
 *
 * The exported `useT` hook reads the current locale from `window.localStorage`
 * (set by the locale switcher) with fallback to Telegram's language tag.
 */

import {
  DEFAULT_LOCALE,
  formatDate as formatDateBase,
  formatNumber as formatNumberBase,
  LOCALES,
  type Locale,
  tFor,
} from "@astrology/i18n";
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "astrovelia.locale";

function detectInitialLocale(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const stored = window.localStorage?.getItem(STORAGE_KEY);
  if (stored && (LOCALES as readonly string[]).includes(stored)) {
    return stored as Locale;
  }
  const tgLang = (
    window as unknown as {
      Telegram?: { WebApp?: { initDataUnsafe?: { user?: { language_code?: string } } } };
    }
  ).Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
  if (tgLang) {
    const head = tgLang.split("-")[0];
    if (head && (LOCALES as readonly string[]).includes(head)) {
      return head as Locale;
    }
  }
  const navLang = navigator.language?.split("-")[0];
  if (navLang && (LOCALES as readonly string[]).includes(navLang)) {
    return navLang as Locale;
  }
  return DEFAULT_LOCALE;
}

const listeners = new Set<(loc: Locale) => void>();
let current: Locale = DEFAULT_LOCALE;
let initialized = false;

function ensureInit() {
  if (initialized) return;
  current = detectInitialLocale();
  initialized = true;
}

export function setLocale(locale: Locale) {
  ensureInit();
  current = locale;
  if (typeof window !== "undefined") {
    window.localStorage?.setItem(STORAGE_KEY, locale);
    document.documentElement.lang = locale;
  }
  listeners.forEach((fn) => {
    fn(locale);
  });
}

export function getLocale(): Locale {
  ensureInit();
  return current;
}

export function useT() {
  const [locale, setLocaleState] = useState<Locale>(() => {
    ensureInit();
    return current;
  });

  useEffect(() => {
    const handler = (loc: Locale) => setLocaleState(loc);
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, []);

  const t = useCallback((key: string) => tFor(locale)(key), [locale]);
  const formatDate = useCallback((d: Date) => formatDateBase(d, locale), [locale]);
  const formatNumber = useCallback((n: number) => formatNumberBase(n, locale), [locale]);

  return { t, locale, setLocale, formatDate, formatNumber };
}
